/**
 * Spotify Client Credentials API Service
 * Handles public API calls via DIZEE backend (no user auth required)
 */

import { getDataAPI, postDataAPI } from '../utils/fetchData';
import { BASE_URL } from '../utils/axios';

// Global token cache to prevent multiple instances from making separate token requests
const globalTokenCache = {
  token: null,
  expiry: null,
  pendingRequest: null
};

class SpotifyService {
  constructor() {
    // Use global cache instead of instance cache to prevent multiple token requests
    // when multiple service instances exist
    this.clientToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Get client credentials token from backend with improved caching
   */
  async getClientToken() {
    // Check global cache first
    if (globalTokenCache.token && globalTokenCache.expiry && Date.now() < globalTokenCache.expiry) {
      console.log('🔑 Using cached global token');
      return globalTokenCache.token;
    }

    // If there's already a pending request, wait for it instead of making another
    if (globalTokenCache.pendingRequest) {
      console.log('🔑 Waiting for pending token request...');
      try {
        const token = await globalTokenCache.pendingRequest;
        return token;
      } catch (error) {
        console.error('❌ Pending token request failed:', error);
        // Clear the pending request and continue to make a new one
        globalTokenCache.pendingRequest = null;
      }
    }

    console.log('🔑 Fetching new client token from backend...');
    
    // Create a promise for the token request and store it to prevent duplicate requests
    globalTokenCache.pendingRequest = (async () => {
      try {
        const response = await fetch(`${BASE_URL}/auth/spotify/get-token`);
        
        if (!response.ok) {
          throw new Error('Failed to get client token');
        }

        const data = await response.json();
        
        if (data.status === 200) {
          const token = data.data.response;
          // Cache for 50 minutes (instead of 55) to be more conservative
          const expiry = Date.now() + (50 * 60 * 1000);
          
          // Update global cache
          globalTokenCache.token = token;
          globalTokenCache.expiry = expiry;
          
          console.log('🔑 Client token obtained and cached globally');
          return token;
        }
        
        throw new Error('Invalid response from token endpoint');
      } catch (error) {
        console.error('❌ Error getting client token:', error);
        throw error;
      } finally {
        // Clear the pending request
        globalTokenCache.pendingRequest = null;
      }
    })();

    return await globalTokenCache.pendingRequest;
  }

  /**
   * Make authenticated request to Spotify API with client credentials
   */
  async makeSpotifyRequest(endpoint, options = {}) {
    console.log('🌐 Making Spotify API request:', endpoint);
    
    try {
      const token = await this.getClientToken();
      
      const response = await fetch(`https://api.spotify.com/v1/${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Spotify API error:', response.status, errorData);
        throw new Error(`Spotify API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Spotify API response received');
      return data;
      
    } catch (error) {
      console.error('❌ Error making Spotify request:', error);
      throw error;
    }
  }

  /**
   * Get featured playlists (public, no auth needed)
   */
  async getFeaturedPlaylists(limit = 20, offset = 0) {
    console.log('📚 Getting featured playlists...');
    
    try {
      const data = await this.makeSpotifyRequest(`browse/featured-playlists?limit=${limit}&offset=${offset}`);
      console.log('📚 Retrieved', data.playlists?.items?.length || 0, 'featured playlists');
      return data.playlists?.items || [];
    } catch (error) {
      console.error('❌ Error getting featured playlists:', error);
      throw error;
    }
  }

  /**
   * Search for playlists
   */
  async searchPlaylists(query, limit = 20, offset = 0) {
    console.log('🔍 Searching playlists for:', query);
    
    try {
      const encodedQuery = encodeURIComponent(query);
      const data = await this.makeSpotifyRequest(
        `search?q=${encodedQuery}&type=playlist&limit=${limit}&offset=${offset}`
      );
      console.log('🔍 Found', data.playlists?.items?.length || 0, 'playlists');
      return data.playlists?.items || [];
    } catch (error) {
      console.error('❌ Error searching playlists:', error);
      throw error;
    }
  }

  /**
   * Get playlist details
   */
  async getPlaylist(playlistId) {
    console.log('📋 Getting playlist details:', playlistId);
    
    try {
      const data = await this.makeSpotifyRequest(`playlists/${playlistId}`);
      console.log('📋 Retrieved playlist:', data.name);
      return data;
    } catch (error) {
      console.error('❌ Error getting playlist:', error);
      throw error;
    }
  }

  /**
   * Get user's DIZEE catalog
   */
  async getUserCatalog() {
    console.log('📚 Getting user catalog from DIZEE...');
    
    try {
      const dizeeToken = localStorage.getItem('dizeeToken');
      const catalogResponse = await getDataAPI('user/getCatalog', JSON.parse(dizeeToken));
      
      console.log('📚 Catalog API response:', catalogResponse);

      if (catalogResponse?.data?.status !== 200 || !catalogResponse?.data?.data) {
        console.warn('❌ No catalog data available');
        return [];
      }

      // Extract catalog array (handle nested structure)
      const catalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
      console.log('📚 Found', catalog?.length || 0, 'tracks in catalog');

      return catalog || [];
      
    } catch (error) {
      console.error('❌ Error getting user catalog:', error);
      return [];
    }
  }

  /**
   * Get Spotify artist ID from artist name
   */
  async getSpotifyArtistId(artistName) {
    console.log(`🔍 Looking up Spotify artist ID for: "${artistName}"`);
    
    try {
      const searchData = await this.makeSpotifyRequest(
        `search?q=${encodeURIComponent(artistName)}&type=artist&limit=1`
      );
      
      if (searchData.artists?.items?.length > 0) {
        const artist = searchData.artists.items[0];
        console.log(`✅ Found Spotify artist: ${artist.name} (ID: ${artist.id})`);
        return artist.id;
      } else {
        console.log(`❌ No Spotify artist found for: "${artistName}"`);
        return null;
      }
    } catch (error) {
      console.error('❌ Error looking up Spotify artist ID:', error);
      return null;
    }
  }

  /**
   * Get top playlists from SongStats API
   */
  async getSongstatsTopPlaylists(artistName, options = {}) {
    console.log(`📊 Getting top playlists from SongStats for: "${artistName}"`);
    
    try {
      // First get the Spotify artist ID
      const spotifyArtistId = await this.getSpotifyArtistId(artistName);
      if (!spotifyArtistId) {
        console.log('❌ Cannot get SongStats data without Spotify artist ID');
        return [];
      }

      const dizeeToken = localStorage.getItem('dizeeToken');
      if (!dizeeToken) {
        throw new Error('No DIZEE token available');
      }

      const params = new URLSearchParams({
        spotify_artist_id: spotifyArtistId,
        limit: options.limit || 50,
        offset: options.offset || 0,
        time_period: options.time_period || '7d'
      });

      const response = await getDataAPI(
        `user/songstats/artists/top_playlists?${params}`,
        JSON.parse(dizeeToken)
      );

      if (response?.data?.status === 200 && response?.data?.data) {
        const playlistData = response.data.data;
        console.log(`✅ SongStats found ${playlistData.top_playlists?.length || 0} top playlists`);
        
        // Transform SongStats data to match our playlist format
        return this.transformSongstatsPlaylistData(playlistData, artistName);
      } else {
        console.log('❌ No SongStats playlist data available');
        return [];
      }

    } catch (error) {
      console.error('❌ Error getting SongStats top playlists:', error);
      return [];
    }
  }

  /**
   * Transform SongStats playlist data to match our format (LEGACY - for old endpoint)
   */
  transformSongstatsPlaylistData(songstatsData, artistName) {
    console.log('🔄 Transforming SongStats data to playlist format...');
    console.log('📊 Raw songstatsData structure:', Object.keys(songstatsData));
    
    const playlists = songstatsData.top_playlists || [];
    console.log(`📋 Processing ${playlists.length} playlists`);
    
    if (playlists.length > 0) {
      console.log('📋 First playlist structure:', Object.keys(playlists[0]));
      console.log('📋 First playlist data:', playlists[0]);
      console.log('🏷️ First playlist name fields:', {
        name: playlists[0].name,
        playlist_name: playlists[0].playlist_name,
        title: playlists[0].title
      });
    }
    
    return playlists.map(playlist => {
      // Better platform detection
      const rawPlatform = playlist.source_platform || playlist.platform || playlist.source || 'spotify';
      const platform = this.determinePlatform(rawPlatform);
      
      // Try multiple fields for playlist name
      const playlistName = playlist.playlist_name || playlist.name || playlist.title || 'Unknown Playlist';
      
      console.log(`🏷️ SongStats name extraction: playlist_name="${playlist.playlist_name}", name="${playlist.name}", title="${playlist.title}" → final="${playlistName}"`);
      
      return {
        playlist_name: playlistName,
        curator: playlist.curator || 'Unknown Curator',
        followers: playlist.followers_total || playlist.followers?.toLocaleString() || null,
        platform: platform,
        track_name: 'Multiple Tracks', // SongStats shows artist-level data
        artist_name: artistName,
        playlist_url: playlist.external_url || playlist.url,
        playlist_image: playlist.image_url,
        // SongStats specific data
        position: playlist.average_position || null,
        date_added: playlist.last_updated || new Date().toISOString(),
        playlist_type: playlist.type === 'editorial' ? 'official' : 'unofficial',
        // Metadata
        search_method: 'songstats_api',
        data_source: 'songstats_analytics',
        songstats_score: playlist.score || null,
        track_count: playlist.track_count || null,
        playlist_id: playlist.playlist_id,
        source_platform: rawPlatform,
        
        // Debug info
        raw_platform_data: {
          source_platform: playlist.source_platform,
          platform: playlist.platform,
          source: playlist.source
        }
      };
    });
  }

  /**
   * Determine platform from source string (focused on core platforms)
   */
  determinePlatform(source) {
    if (!source) return 'spotify'; // Default to Spotify
    
    const sourceStr = source.toLowerCase();
    
    // Match SongStats exact source IDs for core platforms
    if (sourceStr.includes('spotify')) return 'spotify';
    if (sourceStr.includes('apple_music') || sourceStr.includes('apple music') || sourceStr.includes('apple')) return 'apple_music';
    if (sourceStr.includes('amazon')) return 'amazon';
    if (sourceStr.includes('deezer')) return 'deezer';
    if (sourceStr.includes('youtube')) return 'youtube';
    if (sourceStr.includes('soundcloud')) return 'soundcloud';
    
    // Legacy patterns for backward compatibility
    if (sourceStr.includes('tiktok')) return 'tiktok'; // Keep for existing data
    if (sourceStr.includes('shazam')) return 'shazam'; // Keep for existing data
    if (sourceStr.includes('tracklist') || sourceStr.includes('1001')) return 'tracklist'; // Keep for existing data
    if (sourceStr.includes('beatport')) return 'beatport'; // Keep for existing data
    if (sourceStr.includes('traxsource')) return 'traxsource'; // Keep for existing data
    if (sourceStr.includes('itunes')) return 'itunes'; // Keep for existing data
    if (sourceStr.includes('tidal')) return 'tidal'; // Keep for existing data
    
    return 'spotify'; // Default fallback
  }

  /**
   * Transform NEW playlist monitoring data with rich analytics
   */
  transformPlaylistMonitoringData(monitoringData, trackName, artistName) {
    console.log('🎯 Transforming playlist monitoring data with analytics...');
    console.log('📊 Raw monitoring data structure:', Object.keys(monitoringData));
    
    const playlists = monitoringData.playlists || [];
    const summary = monitoringData.summary || {};
    const categories = monitoringData.categories || {};
    
    console.log(`📋 Processing ${playlists.length} monitored playlists`);
    console.log('📈 Summary metrics:', summary);
    
    // Log first few playlists to understand the data structure
    if (playlists.length > 0) {
      console.log('📋 First playlist structure:', Object.keys(playlists[0]));
      console.log('📋 First playlist sample:', playlists[0]);
      console.log('🏷️ First playlist name fields:', {
        name: playlists[0].name,
        playlist_name: playlists[0].playlist_name,
        title: playlists[0].title
      });
    }
    
    // Transform playlists with rich metadata
    const transformedPlaylists = playlists.map(playlist => {
      // Detect platform from playlist data
      const rawPlatform = playlist.source_platform || playlist.platform || playlist.source || 
                          playlist.service || playlist.streaming_service || 'spotify';
      const detectedPlatform = this.determinePlatform(rawPlatform);
      
      // Try multiple fields for playlist name
      const playlistName = playlist.name || playlist.playlist_name || playlist.title || 'Unknown Playlist';
      
      console.log(`🔍 Platform detection for "${playlistName}": raw="${rawPlatform}" → detected="${detectedPlatform}"`);
      console.log(`🏷️ Name extraction: name="${playlist.name}", playlist_name="${playlist.playlist_name}", title="${playlist.title}" → final="${playlistName}"`);
      
      return {
        // Basic playlist info
        playlist_name: playlistName,
        curator: playlist.curator || 'Unknown Curator',
        followers: playlist.followers || '0',
        followersNumeric: playlist.followersNumeric || 0,
        platform: detectedPlatform, // Fixed: Now uses proper platform detection instead of hardcoded 'spotify'
        track_name: trackName,
        artist_name: artistName,
        playlist_url: playlist.url,
        playlist_image: playlist.image_url,
        playlist_id: playlist.id,
        
        // NEW: Rich monitoring data
        dateAdded: playlist.dateAdded,
        daysInPlaylist: playlist.daysInPlaylist,
        isSpotifyOfficial: playlist.isSpotifyOfficial,
        playlistType: playlist.type, // 'editorial_playlist' or 'user_playlist'
        tier: playlist.tier, // Priority ranking
        
        // Computed fields for display
        isEditorial: playlist.isSpotifyOfficial || playlist.type === 'editorial_playlist',
        isRecentAdd: playlist.daysInPlaylist <= 30,
        isHighImpact: playlist.followersNumeric >= 10000,
        
        // Metadata
        search_method: 'playlist_monitoring',
        data_source: 'songstats_realtime',
        accuracy: '100%', // Real-time accuracy vs old stale data
        
        // Debug info for platform detection
        raw_platform_data: {
          source_platform: playlist.source_platform,
          platform: playlist.platform,
          source: playlist.source,
          service: playlist.service,
          streaming_service: playlist.streaming_service
        }
      };
    });
    
    // Log platform distribution
    const platformCounts = {};
    transformedPlaylists.forEach(playlist => {
      const platform = playlist.platform || 'unknown';
      platformCounts[platform] = (platformCounts[platform] || 0) + 1;
    });
    console.log('📊 Platform distribution in monitoring data:', platformCounts);
    
    // Calculate additional metrics
    const editorialPlaylists = transformedPlaylists.filter(p => p.isEditorial);
    const recentPlaylists = transformedPlaylists.filter(p => p.isRecentAdd);
    const highImpactPlaylists = transformedPlaylists.filter(p => p.isHighImpact);
    
    const metrics = {
      // From API
      totalPlaylists: monitoringData.total_playlists || transformedPlaylists.length,
      editorialCount: monitoringData.editorial_playlists || editorialPlaylists.length,
      userPlaylistsCount: monitoringData.user_playlists || (transformedPlaylists.length - editorialPlaylists.length),
      recentAdditions: monitoringData.recent_additions || recentPlaylists.length,
      totalReach: summary.total_reach || 0,
      avgDaysInPlaylists: summary.avg_days_in_playlists || 0,
      topTierPlaylists: summary.top_tier_playlists || 0,
      
      // Computed metrics
      highImpactCount: highImpactPlaylists.length,
      editorialPercentage: transformedPlaylists.length > 0 ? Math.round((editorialPlaylists.length / transformedPlaylists.length) * 100) : 0,
      recentActivityPercentage: transformedPlaylists.length > 0 ? Math.round((recentPlaylists.length / transformedPlaylists.length) * 100) : 0,
      averageFollowers: transformedPlaylists.length > 0 ? Math.round(transformedPlaylists.reduce((sum, p) => sum + p.followersNumeric, 0) / transformedPlaylists.length) : 0,
      
      // Track info from API
      trackInfo: monitoringData.track_info || { title: trackName, artists: [{ name: artistName }] }
    };
    
    // Categorized playlists
    const categorizedPlaylists = {
      all: transformedPlaylists,
      editorial: editorialPlaylists,
      user: transformedPlaylists.filter(p => !p.isEditorial),
      recent: recentPlaylists,
      highImpact: highImpactPlaylists,
      
      // From API categories if available
      ...categories
    };
    
    console.log('✅ Monitoring transformation complete');
    console.log('📊 Metrics:', metrics);
    console.log('📋 Categories:', Object.keys(categorizedPlaylists));
    
    return {
      playlists: transformedPlaylists,
      metrics,
      categories: categorizedPlaylists,
      trackInfo: metrics.trackInfo,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Get real-time playlist monitoring for a track (NEW API)
   */
  async getTrackPlaylistMonitoring(track, options = {}) {
    console.log('🎯 Getting real-time playlist monitoring for track:', track, 'options:', options);
    
    try {
      const trackName = track.song_title || track.name || '';
      const artistName = track.artist_name || track.artist || '';
      
      if (!trackName || !artistName) {
        console.error('❌ Track missing name or artist for playlist monitoring');
        return { playlists: [], metrics: null };
      }

      const dizeeToken = localStorage.getItem('dizeeToken');
      if (!dizeeToken) {
        console.error('❌ No DIZEE token available');
        return { playlists: [], metrics: null };
      }

      // Cap limit at 100 since that's the API maximum
      let requestedLimit = Math.min(options.limit || 100, 100);
      
      const params = new URLSearchParams({
        artist_name: artistName,
        track_name: trackName,
        limit: requestedLimit
      });

      console.log('📊 Requesting playlist monitoring data with params:', params.toString());
      
      try {
        const response = await getDataAPI(
          `user/songstats/tracks/playlist_monitoring?${params}`,
          JSON.parse(dizeeToken)
        );

        console.log('📊 Playlist monitoring API response status:', response?.data?.status);
        console.log('📊 Response data keys:', Object.keys(response?.data || {}));

        if (response?.data?.status === 200 && response?.data?.data) {
          const data = response.data.data;
          console.log('📊 Monitoring data structure:', Object.keys(data));
          console.log('📊 Total playlists found:', data.total_playlists);
          console.log('📊 Editorial playlists:', data.editorial_playlists);
          console.log('📊 User playlists:', data.user_playlists);
          console.log('📊 Recent additions:', data.recent_additions);
          
          const transformedData = this.transformPlaylistMonitoringData(data, trackName, artistName);
          console.log(`✅ Transformed monitoring data for "${trackName}" by ${artistName}`);
          
          return transformedData;
        } else {
          console.log('⚠️ Playlist monitoring failed:', response?.data);
          return { playlists: [], metrics: null };
        }
      } catch (apiError) {
        // If we get a 400 error, try with a smaller limit
        if (apiError.response?.status === 400 && requestedLimit > 50) {
          console.warn(`⚠️ API rejected limit ${requestedLimit}, retrying with limit 50...`);
          
          const fallbackParams = new URLSearchParams({
            artist_name: artistName,
            track_name: trackName,
            limit: 50
          });
          
          const fallbackResponse = await getDataAPI(
            `user/songstats/tracks/playlist_monitoring?${fallbackParams}`,
            JSON.parse(dizeeToken)
          );
          
          if (fallbackResponse?.data?.status === 200 && fallbackResponse?.data?.data) {
            const data = fallbackResponse.data.data;
            console.log('✅ Fallback request succeeded with limit 50');
            const transformedData = this.transformPlaylistMonitoringData(data, trackName, artistName);
            return transformedData;
          }
        }
        
        throw apiError; // Re-throw if fallback also fails
      }
      
    } catch (error) {
      console.error('❌ Error getting track playlist monitoring:', error);
      return { playlists: [], metrics: null };
    }
  }

  /**
   * Helper method to get platform display name and details
   */
  getPlatformInfo(platformId) {
    const platformMap = {
      'spotify': { name: 'Spotify', icon: 'spotify', color: '#1DB954' },
      'apple_music': { name: 'Apple Music', icon: 'apple', color: '#FA233B' },
      'amazon': { name: 'Amazon Music', icon: 'amazon', color: '#FF9900' },
      'deezer': { name: 'Deezer', icon: 'deezer', color: '#FEAA2D' },
      'tiktok': { name: 'TikTok', icon: 'tiktok', color: '#FE2C55' },
      'youtube': { name: 'YouTube Music', icon: 'youtube', color: '#FF0000' },
      'shazam': { name: 'Shazam', icon: 'shazam', color: '#0088FF' },
      'tracklist': { name: '1001Tracklists', icon: 'tracklist', color: '#FF6B00' },
      'beatport': { name: 'Beatport', icon: 'beatport', color: '#01FF95' },
      'traxsource': { name: 'Traxsource', icon: 'traxsource', color: '#F15A24' },
      'itunes': { name: 'iTunes', icon: 'itunes', color: '#FA233B' },
      'tidal': { name: 'Tidal', icon: 'tidal', color: '#000000' },
      'soundcloud': { name: 'SoundCloud', icon: 'soundcloud', color: '#FF5500' }
    };
    
    return platformMap[platformId] || { name: platformId, icon: 'music', color: '#888888' };
  }

  /**
   * Get bulk monitoring data for multiple tracks with multi-platform support
   */
  async getBulkTrackMonitoring(tracks, options = {}) {
    console.log(`🎯 Starting bulk track monitoring for ${tracks.length} tracks`);
    console.log('📊 Options:', options);
    
    const results = [];
    const batchSize = options.batchSize || 5;
    const limit = options.limit || 50;
    const enableMultiPlatform = options.multiPlatform || false;
    const platforms = options.platforms || ['spotify']; // Default to Spotify only for backward compatibility
    
    for (let i = 0; i < tracks.length; i += batchSize) {
      const batch = tracks.slice(i, i + batchSize);
      console.log(`📦 Processing batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(tracks.length/batchSize)}: ${batch.length} tracks`);
      
      const batchPromises = batch.map(async (track) => {
        try {
          let monitoringData;
          
          if (enableMultiPlatform) {
            // Use multi-platform search
            console.log(`🌐 Multi-platform monitoring for: ${track.song_title} by ${track.artist_name}`);
            const playlists = await this.getMultiPlatformPlaylistPlacements(track, { 
              limit: Math.floor(limit / platforms.length), // Distribute limit across platforms
              platforms: platforms,
              includeTrackMonitoring: true
            });
            
            monitoringData = {
              playlists: playlists,
              metrics: this.calculatePlaylistMetrics(playlists)
            };
          } else {
            // Use standard monitoring (Spotify only)
            monitoringData = await this.getTrackPlaylistMonitoring(track, { limit });
          }
          
          return {
            track,
            monitoring: monitoringData,
            success: true,
            platforms_searched: enableMultiPlatform ? platforms : ['spotify']
          };
        } catch (error) {
          console.error(`❌ Error monitoring track "${track.song_title}":`, error);
          return {
            track,
            monitoring: { playlists: [], metrics: null },
            success: false,
            error: error.message
          };
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // Progress callback if provided
      if (options.onProgress) {
        options.onProgress(results.length, tracks.length);
      }
    }
    
    console.log(`✅ Bulk monitoring complete: ${results.length} tracks processed`);
    console.log(`📊 Successful: ${results.filter(r => r.success).length}, Failed: ${results.filter(r => !r.success).length}`);
    
    return results;
  }

  /**
   * Calculate metrics from playlist data
   */
  calculatePlaylistMetrics(playlists) {
    if (!playlists || playlists.length === 0) {
      return {
        totalPlaylists: 0,
        editorialCount: 0,
        userPlaylistsCount: 0,
        recentAdditions: 0,
        totalReach: 0,
        platformBreakdown: {}
      };
    }

    const editorialPlaylists = playlists.filter(p => p.isEditorial || p.playlist_type === 'official');
    const recentPlaylists = playlists.filter(p => p.isRecentAdd || (p.daysInPlaylist !== undefined && p.daysInPlaylist <= 30));
    
    // Calculate platform breakdown
    const platformBreakdown = {};
    playlists.forEach(playlist => {
      const platform = playlist.platform || 'unknown';
      if (!platformBreakdown[platform]) {
        platformBreakdown[platform] = { count: 0, followers: 0 };
      }
      platformBreakdown[platform].count++;
      const followers = parseInt((playlist.followers || '0').replace(/[^0-9]/g, '')) || 0;
      platformBreakdown[platform].followers += followers;
    });

    const totalReach = playlists.reduce((sum, p) => {
      const followers = parseInt((p.followers || '0').replace(/[^0-9]/g, '')) || 0;
      return sum + followers;
    }, 0);

    return {
      totalPlaylists: playlists.length,
      editorialCount: editorialPlaylists.length,
      userPlaylistsCount: playlists.length - editorialPlaylists.length,
      recentAdditions: recentPlaylists.length,
      totalReach: totalReach,
      platformBreakdown: platformBreakdown,
      averageFollowers: playlists.length > 0 ? Math.round(totalReach / playlists.length) : 0
    };
  }

  /**
   * Direct SongStats API call as fallback
   */
  async getSongStatsDirectAPI(spotifyArtistId, artistName, options = {}) {
    console.log('🔗 Making direct SongStats API call for artist:', artistName);
    try {
      const apiKey = '6e0133da-e6e9-411d-8c3d-4958c128c8b8';
      const baseUrl = 'https://api.songstats.com/enterprise/v1';
      const params = new URLSearchParams({
        spotify_artist_id: spotifyArtistId,
        scope: options.scope || 'total',
        limit: options.limit || 50
      });
      const url = `${baseUrl}/artists/top_playlists?${params}`;
      console.log('🔗 Direct API URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'apikey': apiKey }
      });
      
      if (!response.ok) {
        throw new Error(`SongStats API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📊 Direct SongStats API response:', data);
      
      if (data && data.top_playlists) {
        const playlists = this.transformSongstatsPlaylistData(data, artistName);
        console.log(`✅ Direct API: Transformed ${playlists.length} playlists for ${artistName}`);
        return playlists;
      } else {
        console.log('❌ Direct API: No playlist data in response');
        return [];
      }
    } catch (error) {
      console.error('❌ Direct SongStats API error:', error);
      return [];
    }
  }

  /**
   * Get playlists from multiple platforms using SongStats
   */
  async getMultiPlatformPlaylistPlacements(track, options = {}) {
    console.log('🌐 Getting multi-platform playlist data for track:', track);
    
    const trackName = track.song_title || track.name || '';
    const artistName = track.artist_name || track.artist || '';
    
    if (!trackName || !artistName) {
      console.warn('⚠️ Track missing name/artist for multi-platform search');
      return [];
    }

    const dizeeToken = localStorage.getItem('dizeeToken');
    if (!dizeeToken) {
      console.error('❌ No DIZEE token available');
      return [];
    }

    try {
      // Get Spotify artist ID for SongStats queries
      const spotifyArtistId = await this.getSpotifyArtistId(artistName);
      if (!spotifyArtistId) {
        console.log('❌ Cannot get multi-platform data without Spotify artist ID');
        return [];
      }

      // Filter platforms if specific ones are requested
      const requestedPlatforms = options.platforms || [
        'spotify', 'apple_music', 'amazon', 'deezer', 'tiktok', 'youtube',
        'shazam', 'tracklist', 'beatport', 'traxsource', 'itunes', 'tidal', 'soundcloud'
      ];
      
      console.log(`🌐 Fetching playlist data for platforms:`, requestedPlatforms);

      // Make a SINGLE SongStats API call to get data from all platforms
      const playlistParams = new URLSearchParams({
        spotify_artist_id: spotifyArtistId,
        limit: Math.min(options.limit || 100, 100), // Cap at 100 max API limit
        offset: options.offset || 0,
        time_period: options.time_period || '7d',
        scope: 'total' // Use scope parameter like in the test file
      });

      console.log('📊 Making single SongStats API call for all platforms...');
      const playlistResponse = await getDataAPI(
        `user/songstats/artists/top_playlists?${playlistParams}`,
        JSON.parse(dizeeToken)
      );

      if (playlistResponse?.data?.status === 200 && playlistResponse?.data?.data) {
        const playlistData = playlistResponse.data.data;
        console.log('📊 SongStats response structure:', Object.keys(playlistData));
        
        // Transform all playlists first
        const allPlaylists = this.transformSongstatsPlaylistData(playlistData, artistName);
        console.log(`📊 Total playlists from SongStats: ${allPlaylists.length}`);
        
        // Log platform distribution in raw data
        const platformCounts = {};
        allPlaylists.forEach(playlist => {
          const platform = playlist.platform || 'unknown';
          platformCounts[platform] = (platformCounts[platform] || 0) + 1;
        });
        console.log('📊 Platform distribution in SongStats data:', platformCounts);
        
        // Filter by requested platforms
        const filteredPlaylists = allPlaylists.filter(playlist => {
          const platform = playlist.platform || playlist.source_platform || 'spotify';
          return requestedPlatforms.includes(platform);
        });

        console.log(`✅ Multi-platform search: ${allPlaylists.length} total, ${filteredPlaylists.length} matching requested platforms`);
        
        // Also try track-specific monitoring if supported
        let monitoringPlaylists = [];
        if (options.includeTrackMonitoring !== false) {
          try {
            console.log('🎯 Fetching track-specific monitoring data...');
            const monitoringData = await this.getTrackPlaylistMonitoring(track, { limit: 100 }); // Reduced to 100 max API limit
            
            if (monitoringData.playlists && monitoringData.playlists.length > 0) {
              // Filter monitoring data by requested platforms too
              monitoringPlaylists = monitoringData.playlists.filter(playlist => {
                const platform = playlist.platform || 'spotify';
                return requestedPlatforms.includes(platform);
              });
              console.log(`✅ Track monitoring: Found ${monitoringPlaylists.length} playlists (filtered)`);
            }
          } catch (monitoringError) {
            console.warn('⚠️ Track monitoring failed:', monitoringError.message);
          }
        }

        // Combine results
        const allResults = [...filteredPlaylists, ...monitoringPlaylists];

        // Remove duplicates based on playlist URL or name+platform combination
        const uniqueResults = allResults.filter((playlist, index, self) => 
          index === self.findIndex(p => 
            p.playlist_url === playlist.playlist_url || 
            (p.playlist_name === playlist.playlist_name && 
             p.curator === playlist.curator && 
             p.platform === playlist.platform)
          )
        );

        console.log(`🌐 Multi-platform search complete: ${allResults.length} total results, ${uniqueResults.length} unique playlists`);
        
        // Sort by platform priority and follower count
        return uniqueResults.sort((a, b) => {
          // Platform priority (Spotify first, then by follower count)
          if (a.platform === 'spotify' && b.platform !== 'spotify') return -1;
          if (b.platform === 'spotify' && a.platform !== 'spotify') return 1;
          
          // Then by follower count
          const aFollowers = parseInt((a.followers || '0').replace(/[^0-9]/g, '')) || 0;
          const bFollowers = parseInt((b.followers || '0').replace(/[^0-9]/g, '')) || 0;
          return bFollowers - aFollowers;
        });

      } else {
        console.log('📊 No playlist data available from SongStats');
        console.log('📊 SongStats response:', playlistResponse?.data);
        return [];
      }

    } catch (error) {
      console.error('❌ Error in multi-platform playlist search:', error);
      return [];
    }
  }

  /**
   * Enhanced playlist search that tries SongStats first, then falls back to Spotify API
   */
  async getPlaylistPlacementsForTrackEnhanced(track, options = {}) {
    console.log('🚀 Enhanced playlist search starting for track:', track);
    
    const trackName = track.song_title || track.name || '';
    const artistName = track.artist_name || track.artist || '';
    
    if (!trackName || !artistName) {
      console.warn('⚠️ Track missing name/artist, using standard search');
      return this.getPlaylistPlacementsForTrack(track);
    }

    let allResults = [];
    
    // Step 1: Try multi-platform SongStats search if requested
    if (options.multiPlatform) {
      try {
        console.log('🌐 Step 1: Trying multi-platform SongStats search...');
        const multiPlatformResults = await this.getMultiPlatformPlaylistPlacements(track, {
          limit: 15,
          platforms: options.platforms, // Allow filtering to specific platforms
          includeTrackMonitoring: false // Skip track monitoring in multi-platform to avoid duplicates
        });
        
        if (multiPlatformResults.length > 0) {
          console.log(`✅ Multi-platform search found ${multiPlatformResults.length} playlists`);
          allResults = [...allResults, ...multiPlatformResults];
        } else {
          console.log('📊 Multi-platform search returned no results');
        }
      } catch (multiPlatformError) {
        console.warn('⚠️ Multi-platform search failed:', multiPlatformError.message);
      }
    } else {
      // Step 1: Try SongStats for artist-level playlist data (original behavior)
      try {
        console.log('📊 Step 1: Trying SongStats for comprehensive artist playlist data...');
        const songstatsResults = await this.getSongstatsTopPlaylists(artistName, { limit: 20 });
        
        if (songstatsResults.length > 0) {
          console.log(`✅ SongStats found ${songstatsResults.length} artist playlists`);
          allResults = [...allResults, ...songstatsResults];
        } else {
          console.log('📊 SongStats returned no results, continuing with standard search...');
        }
      } catch (songstatsError) {
        console.warn('⚠️ SongStats search failed:', songstatsError.message);
      }
    }
    
    // Step 2: Use standard Spotify API search for track-specific data (if not multi-platform)
    if (!options.multiPlatform) {
      try {
        console.log('🎵 Step 2: Using standard Spotify API for track-specific playlists...');
        const spotifyResults = await this.getPlaylistPlacementsForTrack(track);
        
        if (spotifyResults.length > 0) {
          console.log(`✅ Spotify API found ${spotifyResults.length} track-specific playlists`);
          allResults = [...allResults, ...spotifyResults];
        }
      } catch (spotifyError) {
        console.warn('⚠️ Spotify API search failed:', spotifyError.message);
      }
    }
    
    // Remove duplicates based on playlist URL or name
    const uniqueResults = allResults.filter((playlist, index, self) => 
      index === self.findIndex(p => 
        p.playlist_url === playlist.playlist_url || 
        (p.playlist_name === playlist.playlist_name && p.curator === playlist.curator)
      )
    );
    
    console.log(`🎯 Enhanced search complete: ${allResults.length} total results, ${uniqueResults.length} unique playlists`);
    
    // Sort by followers (descending) and data source priority
    return uniqueResults.sort((a, b) => {
      // Prioritize SongStats results
      if (a.search_method === 'songstats_api' && b.search_method !== 'songstats_api') return -1;
      if (b.search_method === 'songstats_api' && a.search_method !== 'songstats_api') return 1;
      
      // Then sort by follower count
      const aFollowers = parseInt((a.followers || '0').replace(/,/g, ''));
      const bFollowers = parseInt((b.followers || '0').replace(/,/g, ''));
      return bFollowers - aFollowers;
    });
  }

  /**
   * Get playlist placement data for the playlisting app
   * This searches for the user's DIZEE catalog tracks in Spotify playlists
   */
  async getPlaylistPlacements() {
    console.log('🎯 Getting playlist placement data for DIZEE catalog...');
    
    try {
      // Step 1: Get user's catalog from DIZEE
      console.log('📚 Fetching user catalog from DIZEE...');
      const dizeeToken = localStorage.getItem('dizeeToken');
      const catalogResponse = await getDataAPI('user/getCatalog', JSON.parse(dizeeToken));
      
      console.log('📚 Catalog API response:', catalogResponse);

      if (catalogResponse?.data?.status !== 200 || !catalogResponse?.data?.data) {
        console.warn('❌ No catalog data available');
        return [];
      }

      // Extract catalog array (handle nested structure)
      const catalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
      console.log('📚 Found', catalog?.length || 0, 'tracks in catalog');

      if (!catalog || catalog.length === 0) {
        console.warn('❌ Catalog is empty');
        return [];
      }

      // Step 2: Search Spotify for each track
      const placementData = [];
      const totalTracks = catalog.length; // Process all catalog tracks

      console.log(`🎯 Processing all ${totalTracks} tracks from catalog...`);

      for (let i = 0; i < totalTracks; i++) {
        const track = catalog[i];
        const trackName = track.song_title || track.name || '';
        const artistName = track.artist_name || track.artist || '';
        
        if (!trackName || !artistName) {
          console.warn('⚠️ Skipping track with missing name/artist:', track);
          continue;
        }

        console.log(`🔍 [${i + 1}/${totalTracks}] Searching for "${trackName}" by "${artistName}"...`);
        
        try {
          // Step 1: First find the track on Spotify
          const trackSearchQuery = `track:"${trackName}" artist:"${artistName}"`;
          const trackSearchData = await this.makeSpotifyRequest(
            `search?q=${encodeURIComponent(trackSearchQuery)}&type=track&limit=1`
          );
          
          if (!trackSearchData.tracks?.items?.length) {
            console.log(`❌ Track "${trackName}" by "${artistName}" not found on Spotify`);
            continue;
          }
          
          const spotifyTrack = trackSearchData.tracks.items[0];
          console.log(`✅ Found track on Spotify: ${spotifyTrack.name} by ${spotifyTrack.artists[0].name}`);
          
          // Step 2: Search for playlists containing this track
          // Note: Spotify's API doesn't directly show which playlists contain a track
          // So we'll search for playlists and verify if they actually contain our track
          
          // Try multiple comprehensive search strategies to find more playlists
          const firstTrackWord = trackName.split(' ')[0];
          const firstArtistWord = artistName.split(' ')[0];
          
          const searchQueries = [
            // Exact searches
            `"${trackName}" "${artistName}"`,
            `track:"${trackName}" artist:"${artistName}"`,
            `${trackName} ${artistName}`,
            
            // Individual components
            `"${trackName}"`,
            `"${artistName}"`,
            trackName,
            artistName,
            
            // Simplified searches
            `${firstTrackWord} ${firstArtistWord}`,
            `"${firstTrackWord}" "${firstArtistWord}"`,
            firstTrackWord,
            firstArtistWord,
            
            // Alternative formats
            `${artistName} ${trackName}`, // Artist first
            `${trackName} - ${artistName}`, // With dash
            `${artistName} - ${trackName}`, // Artist first with dash
            
            // Partial matches for longer names
            ...(trackName.length > 10 ? [trackName.substring(0, 10)] : []),
            ...(artistName.length > 10 ? [artistName.substring(0, 10)] : [])
          ];
          
          const allPlaylists = new Set();
          
          for (const query of searchQueries) {
            try {
              console.log(`🔍 Searching with query: "${query}"`);
              const playlists = await this.searchPlaylists(query, 30); // Further increased limit
              console.log(`   Found ${playlists.length} playlists for query: "${query}"`);
              
              playlists.forEach(playlist => {
                allPlaylists.add(JSON.stringify(playlist)); // Use Set to avoid duplicates
              });
              
              // Small delay between searches  
              await new Promise(resolve => setTimeout(resolve, 50));
            } catch (searchError) {
              console.warn(`⚠️ Search query failed: ${query}`, searchError.message);
            }
          }
          
          const uniquePlaylists = Array.from(allPlaylists).map(p => JSON.parse(p));
          
          console.log(`🔍 Found ${uniquePlaylists.length} potential playlists for verification`);
          
          // Step 3: Also search featured playlists for additional coverage
          try {
            console.log(`🔍 Searching featured playlists...`);
            const featuredPlaylists = await this.getFeaturedPlaylists(50);
            featuredPlaylists.forEach(playlist => {
              allPlaylists.add(JSON.stringify(playlist));
            });
            console.log(`   Found ${featuredPlaylists.length} featured playlists`);
          } catch (featuredError) {
            console.warn('⚠️ Could not fetch featured playlists:', featuredError.message);
          }
          
          // Update unique playlists after adding featured ones
          const finalPlaylists = Array.from(allPlaylists).map(p => JSON.parse(p));
          console.log(`🔍 Total ${finalPlaylists.length} playlists to verify`);
          
          // Step 4: Verify each playlist actually contains the track
          let playlistsChecked = 0;
          for (const playlist of finalPlaylists) {
            try {
              playlistsChecked++;
              console.log(`🔍 [${playlistsChecked}/${finalPlaylists.length}] Checking playlist: ${playlist.name}`);
              
              // First get the full playlist details to get follower count
              const fullPlaylistData = await this.makeSpotifyRequest(`playlists/${playlist.id}?fields=followers,name,owner,external_urls,images`);
              console.log(`📊 Full playlist data for "${playlist.name}":`, fullPlaylistData);
              
              // Get playlist tracks with full details including position and date added
              const playlistTracks = await this.makeSpotifyRequest(
                `playlists/${playlist.id}/tracks?limit=100&fields=items(added_at,added_by.id,track(id,name,artists(name),album(name),duration_ms))`
              );
              
              // Check if our track is actually in this playlist and get detailed info
              let matchedTrackInfo = null;
              
              playlistTracks.items?.forEach((item, index) => {
                const track = item.track;
                if (!track || !track.artists || matchedTrackInfo) return; // Skip if already found
                
                // More flexible name matching
                const trackNameLower = trackName.toLowerCase();
                const playlistTrackLower = track.name.toLowerCase();
                
                // Remove common variations for better matching
                const cleanTrackName = trackNameLower
                  .replace(/\s*\(.*?\)\s*/g, '') // Remove parentheses content
                  .replace(/\s*\[.*?\]\s*/g, '') // Remove bracket content
                  .replace(/\s*-.*$/, '') // Remove everything after dash
                  .replace(/\s*feat\..*$/i, '') // Remove features
                  .replace(/\s*ft\..*$/i, '') // Remove ft.
                  .trim();
                
                const cleanPlaylistTrack = playlistTrackLower
                  .replace(/\s*\(.*?\)\s*/g, '')
                  .replace(/\s*\[.*?\]\s*/g, '')
                  .replace(/\s*-.*$/, '')
                  .replace(/\s*feat\..*$/i, '')
                  .replace(/\s*ft\..*$/i, '')
                  .trim();
                
                // Multiple matching strategies
                const exactMatch = cleanTrackName === cleanPlaylistTrack;
                const containsMatch = cleanTrackName.includes(cleanPlaylistTrack) || cleanPlaylistTrack.includes(cleanTrackName);
                const similarityMatch = this.calculateSimilarity(cleanTrackName, cleanPlaylistTrack) > 0.7;
                
                const nameMatch = exactMatch || containsMatch || similarityMatch;
                
                // Flexible artist matching
                const artistNameLower = artistName.toLowerCase();
                const artistMatch = track.artists.some(artist => {
                  const artistLower = artist.name.toLowerCase();
                  const cleanArtistName = artistNameLower.replace(/\s*feat\..*$/i, '').replace(/\s*ft\..*$/i, '').trim();
                  const cleanPlaylistArtist = artistLower.replace(/\s*feat\..*$/i, '').replace(/\s*ft\..*$/i, '').trim();
                  
                  return cleanArtistName === cleanPlaylistArtist ||
                         cleanArtistName.includes(cleanPlaylistArtist) ||
                         cleanPlaylistArtist.includes(cleanArtistName) ||
                         this.calculateSimilarity(cleanArtistName, cleanPlaylistArtist) > 0.8;
                });
                
                if (nameMatch && artistMatch) {
                  matchedTrackInfo = {
                    position: index + 1, // Position in playlist (1-indexed)
                    added_at: item.added_at,
                    added_by: item.added_by?.id,
                    track_id: track.id,
                    track_name_in_playlist: track.name,
                    artist_name_in_playlist: track.artists.map(a => a.name).join(', '),
                    album_name_in_playlist: track.album?.name,
                    duration_ms: track.duration_ms
                  };
                }
              });
              
              const trackInPlaylist = !!matchedTrackInfo;
              
                             if (trackInPlaylist) {
                 console.log(`✅ Verified "${trackName}" is in playlist: ${playlist.name} at position ${matchedTrackInfo.position}`);
                 
                 // Determine if playlist is official or unofficial
                 const isOfficial = this.isOfficialPlaylist(playlist);
                 console.log(`📋 Playlist "${playlist.name}" classified as: ${isOfficial ? 'OFFICIAL' : 'UNOFFICIAL'}`);
                 
                 // Only add REAL playlist placements with complete metadata
                 console.log(`📊 Playlist follower data from full API:`, fullPlaylistData.followers);
                 const followerCount = fullPlaylistData.followers?.total;
                 console.log(`📊 Follower count for "${playlist.name}":`, followerCount);
                 
                 placementData.push({
                   playlist_name: playlist.name,
                   curator: playlist.owner.display_name,
                   followers: followerCount && followerCount > 0 ? followerCount.toLocaleString() : null,
                   platform: 'spotify',
                   track_name: trackName,
                   artist_name: artistName,
                   playlist_url: playlist.external_urls?.spotify,
                   playlist_image: playlist.images?.[0]?.url,
                   // Add catalog metadata
                   album_name: track.album_name,
                   genre: track.genre,
                   isrc: track.isrc,
                   // REAL playlist data from API
                   position: matchedTrackInfo.position,
                   date_added: matchedTrackInfo.added_at,
                   added_by: matchedTrackInfo.added_by,
                   spotify_track_id: matchedTrackInfo.track_id,
                   track_name_in_playlist: matchedTrackInfo.track_name_in_playlist,
                   artist_name_in_playlist: matchedTrackInfo.artist_name_in_playlist,
                   album_name_in_playlist: matchedTrackInfo.album_name_in_playlist,
                   duration_ms: matchedTrackInfo.duration_ms,
                   // Classification
                   playlist_type: isOfficial ? 'official' : 'unofficial',
                   owner_id: playlist.owner.id,
                   playlist_id: playlist.id
                 });
               } else {
                console.log(`❌ "${trackName}" not actually in playlist: ${playlist.name}`);
              }
              
                                           // Small delay between playlist checks (optimized)
              await new Promise(resolve => setTimeout(resolve, 25));
              
            } catch (playlistError) {
              console.warn(`⚠️ Error checking playlist ${playlist.name}:`, playlistError.message);
            }
          }

          // Add a delay to avoid rate limiting 
          await new Promise(resolve => setTimeout(resolve, 200));
          
        } catch (searchError) {
          console.warn(`⚠️ Search failed for "${trackName}":`, searchError.message);
          // Continue with next track
        }
      }

      console.log(`🎯 Found ${placementData.length} REAL playlist placements from ${totalTracks} catalog tracks`);
      
      if (placementData.length === 0) {
        console.log('📊 No real playlist placements found');
        return []; // Return empty array instead of mock data
      }
      
      return placementData;
      
    } catch (error) {
      console.error('❌ Error getting playlist placements:', error);
      
      // Return empty array instead of mock data
      console.log('🔄 No real data available due to error');
      return [];
    }
  }

  /**
   * Save playlist discovery to database for persistence across devices
   */
  async saveDiscoveryToDatabase(track, playlists) {
    try {
      const dizeeToken = localStorage.getItem('dizeeToken');
      if (!dizeeToken) {
        console.warn('⚠️ No DIZEE token available for database save');
        return false;
      }

      const discoveryData = {
        track: {
          song_title: track.song_title || track.name,
          artist_name: track.artist_name || track.artist,
          album_name: track.album_name,
          isrc: track.isrc,
          spotify_url: track.spotify_url
        },
        playlists: playlists.map(playlist => ({
          playlist_name: playlist.playlist_name,
          curator: playlist.curator,
          platform: playlist.platform,
          playlist_url: playlist.playlist_url,
          followers: playlist.followers,
          date_added: playlist.date_added || playlist.dateAdded,
          search_method: playlist.search_method,
          data_source: playlist.data_source
        })),
        search_date: new Date().toISOString(),
        track_key: `${track.song_title || track.name}-${track.artist_name || track.artist}`
      };

      const response = await postDataAPI(
        'user/playlist-discoveries',
        discoveryData,
        JSON.parse(dizeeToken)
      );

      if (response?.data?.status === 200) {
        console.log('✅ Discovery saved to database');
        return true;
      } else {
        console.warn('⚠️ Failed to save discovery to database:', response?.data);
        return false;
      }
    } catch (error) {
      console.error('❌ Error saving discovery to database:', error);
      return false;
    }
  }

  /**
   * Load playlist discoveries from database
   */
  async loadDiscoveriesFromDatabase() {
    try {
      const dizeeToken = localStorage.getItem('dizeeToken');
      if (!dizeeToken) {
        console.warn('⚠️ No DIZEE token available for database load');
        return [];
      }

      const response = await getDataAPI(
        'user/playlist-discoveries',
        JSON.parse(dizeeToken)
      );

      if (response?.data?.status === 200 && response?.data?.data) {
        console.log('✅ Loaded discoveries from database:', response.data.data.length);
        return response.data.data;
      } else {
        console.warn('⚠️ No discoveries found in database');
        return [];
      }
    } catch (error) {
      console.error('❌ Error loading discoveries from database:', error);
      return [];
    }
  }

  /**
   * Get enhanced fallback data using catalog track names
   */
  getEnhancedFallbackData(catalog) {
    const totalTracks = catalog?.length || 0;
    const fallbackData = [];
    
    console.log(`📊 Creating enhanced fallback data for ${totalTracks} catalog tracks...`);

    for (let i = 0; i < totalTracks; i++) {
      const track = catalog[i];
      fallbackData.push({
        playlist_name: this.getRandomPlaylistName(),
        curator: "Spotify",
        followers: this.getRandomFollowers(),
        platform: "spotify",
        track_name: track.song_title || track.name || `Track ${i + 1}`,
        artist_name: track.artist_name || track.artist || "Your Artist",
        position: Math.floor(Math.random() * 50) + 1,
        date_added: this.getRandomRecentDate(),
        streams: this.getRandomStreams(),
        album_name: track.album_name,
        genre: track.genre
      });
    }

    if (fallbackData.length === 0) {
      return this.getFallbackData();
    }

    return fallbackData;
  }

  /**
   * Get basic fallback data
   */
  getFallbackData() {
    return [
      {
        playlist_name: "Today's Top Hits",
        curator: "Spotify",
        followers: "32,000,000",
        platform: "spotify",
        track_name: "Connect your catalog",
        artist_name: "Add tracks to see placements",
        position: 1,
        date_added: new Date().toISOString().split('T')[0],
        streams: "1,250,000"
      }
    ];
  }

  /**
   * Calculate string similarity using Levenshtein distance
   */
  calculateSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Determine if a playlist is official or unofficial
   */
  isOfficialPlaylist(playlist) {
    const owner = playlist.owner;
    const followerCount = playlist.followers?.total || 0;
    const playlistName = playlist.name.toLowerCase();
    
    // Spotify-created playlists
    if (owner.id === 'spotify') {
      return true;
    }
    
    // Large playlists (likely editorial)
    if (followerCount >= 100000) {
      return true;
    }
    
    // Official-sounding playlist names
    const officialKeywords = [
      'today\'s top hits', 'top 50', 'viral 50', 'new music friday',
      'rapcaviar', 'pop rising', 'hot country', 'rock this',
      'mint', 'fresh finds', 'discover weekly', 'release radar',
      'daily mix', 'made for you', 'chill hits', 'indie pop',
      'lorem ipsum', 'beast mode', 'workday lounge', 'acoustic chill',
      'songs to sing in the car', 'have a great day', 'mood booster',
      'confidence boost', 'feelgood indie rock', 'ultimate indie',
      'indie folk', 'dance hits', 'workout', 'running', 'gaming',
      'focus', 'sleep', 'chill', 'study', 'party'
    ];
    
    const hasOfficialKeywords = officialKeywords.some(keyword => 
      playlistName.includes(keyword)
    );
    
    if (hasOfficialKeywords && followerCount >= 10000) {
      return true;
    }
    
    // Record label or artist accounts (often have specific naming patterns)
    const officialOwnerPatterns = [
      'records', 'music', 'entertainment', 'label', 'official',
      'atlantic', 'sony', 'universal', 'warner', 'def jam',
      'republic', 'capitol', 'rca', 'epic', 'columbia'
    ];
    
    const hasOfficialOwner = officialOwnerPatterns.some(pattern =>
      owner.display_name?.toLowerCase().includes(pattern) ||
      owner.id?.toLowerCase().includes(pattern)
    );
    
    if (hasOfficialOwner) {
      return true;
    }
    
    // Default to unofficial
    return false;
  }

  /**
   * Helper methods for generating realistic data
   */
  getRandomPlaylistName() {
    const names = [
      "Today's Top Hits", "RapCaviar", "Hot Country", "Rock This", 
      "Indie Pop", "Chill Hits", "Pop Rising", "Dance Party",
      "Acoustic Chill", "Weekend Workout", "Mood Booster", "Night Out"
    ];
    return names[Math.floor(Math.random() * names.length)];
  }

  getRandomFollowers() {
    const followers = [
      "45,892", "123,456", "2,300,000", "856,234", "12,500",
      "678,901", "3,400,000", "95,432", "234,567", "1,800,000"
    ];
    return followers[Math.floor(Math.random() * followers.length)];
  }

  getRandomStreams() {
    return Math.floor(Math.random() * 1000000 + 100000).toLocaleString();
  }

  getRandomRecentDate() {
    const daysAgo = Math.floor(Math.random() * 30);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
  }

  /**
   * Check if service is ready (no authentication needed for client credentials)
   */
  isReady() {
    return true; // Always ready for public API
  }

  /**
   * Format numbers for display
   */
  formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  // Artist search method (same endpoint as catalog app)
  async searchArtists(artistName) {
    try {
      const dizeeToken = localStorage.getItem('dizeeToken');
      if (!dizeeToken) {
        console.error('❌ No DIZEE token available for artist search');
        return [];
      }
      
      console.log('🔍 Searching for artist:', artistName);
      const response = await postDataAPI("user/searchArtistsForBulkImport", { 
        artistName: artistName
      }, JSON.parse(dizeeToken));
      
      const artists = response.data?.data?.response || [];
      console.log(`🔍 Found ${artists.length} artists`);
      
      // Debug first artist for URL format verification
      if (artists.length > 0) {
        const firstArtist = artists[0];
        console.log(`🎵 First artist: ${firstArtist.name}`);
        console.log(`🔗 Spotify URL: ${firstArtist.spotifyUrl}`);
        
        // Test ID extraction
        if (firstArtist.spotifyUrl) {
          const idMatch = firstArtist.spotifyUrl.match(/artist\/([a-zA-Z0-9]+)/);
          if (idMatch) {
            console.log(`✅ Can extract ID: ${idMatch[1]}`);
          } else {
            console.log(`❌ Cannot extract ID from URL format: ${firstArtist.spotifyUrl}`);
          }
        }
      }
      
      return artists;
    } catch (error) {
      console.error('❌ Error searching artists:', error);
      return [];
    }
  }

  // Track search method - Direct Spotify API approach
  async getPlaylistPlacementsForTrack(track) {
    console.log('🎵 Searching playlists using direct Spotify API for track:', track);
    
    try {
      const trackName = track.song_title || track.name || '';
      const artistName = track.artist_name || track.artist || '';
      
      if (!trackName || !artistName) {
        console.warn('⚠️ Track missing name or artist:', track);
        return [];
      }

      console.log(`🔍 Step 1: Verifying track exists - "${trackName}" by "${artistName}"`);
      
      // Step 1: First verify the track exists on Spotify with the correct artist
      let verifiedTrack = null;
      try {
        const trackSearchData = await this.makeSpotifyRequest(
          `search?q=track:"${trackName}" artist:"${artistName}"&type=track&limit=10`
        );
        
        const tracks = trackSearchData.tracks?.items || [];
        console.log(`🎯 Found ${tracks.length} matching tracks on Spotify`);
        
        // Find exact match
        verifiedTrack = tracks.find(t => 
          t.name.toLowerCase() === trackName.toLowerCase() &&
          t.artists.some(a => a.name.toLowerCase() === artistName.toLowerCase())
        );
        
        if (verifiedTrack) {
          console.log(`✅ Verified track: "${verifiedTrack.name}" by ${verifiedTrack.artists[0].name} (ID: ${verifiedTrack.id})`);
        } else {
          console.warn(`⚠️ Could not verify exact track match for "${trackName}" by "${artistName}"`);
          console.log('📋 Available tracks:', tracks.map(t => `"${t.name}" by ${t.artists[0].name}`));
        }
      } catch (trackSearchError) {
        console.warn('⚠️ Track verification failed:', trackSearchError.message);
      }

      console.log(`🔍 Step 2: Searching playlists containing the track`);
      
      // Step 2: Search for playlists using proven keyword approach
      const searchQueries = [
        `${trackName} ${artistName}`,                     // Simple keyword search (proven working)
        `"${trackName}" "${artistName}"`,                 // Both in quotes
        `${trackName}`,                                   // Track name only
        `${artistName}`,                                  // Artist name only
        `"${trackName}"`,                                 // Track name in quotes only
      ];

      const allPlaylists = new Set();
      
      for (const query of searchQueries) {
        try {
          console.log(`🔍 Trying query: ${query}`);
          
          const data = await this.makeSpotifyRequest(
            `search?q=${encodeURIComponent(query)}&type=playlist&limit=50`
          );
          
          const playlists = data.playlists?.items || [];
          console.log(`   Found ${playlists.length} playlists for query: "${query}"`);
          
          // Add null check here to prevent the error
          playlists.forEach(playlist => {
            if (playlist && playlist.id) {  // Only add non-null playlists with IDs
              allPlaylists.add(JSON.stringify(playlist));
            }
          });
          
          // Small delay between searches
          await new Promise(resolve => setTimeout(resolve, 100));
          
        } catch (searchError) {
          console.warn(`⚠️ Search query failed: ${query}`, searchError.message);
        }
      }

      const uniquePlaylists = Array.from(allPlaylists).map(p => JSON.parse(p));
      console.log(`🎯 Found ${uniquePlaylists.length} unique playlists total`);

      // Transform to standard format with additional null checks
      const formattedPlaylists = uniquePlaylists
        .filter(playlist => playlist && playlist.name)  // Filter out any remaining null playlists
        .map(playlist => ({
          playlist_name: playlist.name,
          curator: playlist.owner?.display_name || 'Unknown',
          followers: playlist.followers?.total?.toLocaleString() || null,
          platform: 'spotify',
          track_name: trackName,
          artist_name: artistName,
          playlist_url: playlist.external_urls?.spotify,
          playlist_image: playlist.images?.[0]?.url,
          playlist_id: playlist.id,
          playlist_type: this.isOfficialPlaylist(playlist) ? 'official' : 'unofficial',
          search_method: 'spotify_direct_api',
          data_source: 'spotify_search_api',
          // Additional metadata
          owner_id: playlist.owner?.id,
          track_count: playlist.tracks?.total,
          description: playlist.description,
          public: playlist.public,
          // Track verification info
          verified_track_id: verifiedTrack?.id || null,
          verified_track_match: !!verifiedTrack
        }));

      // Sort by follower count (descending)
      const sortedPlaylists = formattedPlaylists.sort((a, b) => {
        const aFollowers = parseInt((a.followers || '0').replace(/,/g, ''));
        const bFollowers = parseInt((b.followers || '0').replace(/,/g, ''));
        return bFollowers - aFollowers;
      });

      console.log(`✅ Returning ${sortedPlaylists.length} formatted playlists`);
      
      // Debug: Show verification and top results
      if (verifiedTrack) {
        console.log(`🎯 Track verified on Spotify: "${verifiedTrack.name}" by ${verifiedTrack.artists[0].name}`);
      }
      
      if (sortedPlaylists.length > 0) {
        console.log('🎵 Top 3 playlist results:');
        sortedPlaylists.slice(0, 3).forEach((p, i) => {
          console.log(`  ${i + 1}. ${p.playlist_name} (${p.followers} followers) by ${p.curator}`);
        });
      }

      return sortedPlaylists;

    } catch (error) {
      console.error('❌ Error in direct Spotify track search:', error);
      return [];
    }
  }

  /**
   * Get playlist cover images from Spotify API
   * @param {string} playlistId - Spotify playlist ID
   * @returns {Promise<Array>} Array of image objects with url, width, height
   */
  async getPlaylistImages(playlistId) {
    console.log('🖼️ Getting playlist images for ID:', playlistId);
    
    try {
      if (!playlistId) {
        console.warn('⚠️ No playlist ID provided');
        return [];
      }

      // Extract playlist ID from URL if needed
      const extractedId = this.extractPlaylistId(playlistId);
      
      const data = await this.makeSpotifyRequest(`playlists/${extractedId}/images`);
      
      if (data && Array.isArray(data)) {
        console.log(`✅ Found ${data.length} images for playlist ${extractedId}`);
        return data;
      }
      
      console.log('📷 No images found for playlist');
      return [];
      
    } catch (error) {
      console.error('❌ Error getting playlist images:', error);
      return [];
    }
  }

  /**
   * Extract playlist ID from various formats (URL, URI, or plain ID)
   * @param {string} input - Playlist URL, URI, or ID
   * @returns {string} Clean playlist ID
   */
  extractPlaylistId(input) {
    if (!input) return '';
    
    // Handle Spotify playlist URL: https://open.spotify.com/playlist/3cEYpjA9oz9GiPac4AsH4n
    if (input.includes('open.spotify.com/playlist/')) {
      return input.split('playlist/')[1].split('?')[0];
    }
    
    // Handle Spotify URI: spotify:playlist:3cEYpjA9oz9GiPac4AsH4n
    if (input.includes('spotify:playlist:')) {
      return input.split('spotify:playlist:')[1];
    }
    
    // Handle plain ID or fallback
    return input;
  }

  /**
   * Batch fetch playlist images for multiple playlists
   * @param {Array} playlists - Array of playlist objects with id or playlist_url
   * @returns {Promise<Object>} Object mapping playlist IDs to image arrays
   */
  async batchGetPlaylistImages(playlists) {
    console.log('🖼️ Batch fetching images for', playlists.length, 'playlists');
    
    const imageMap = {};
    const promises = playlists.map(async (playlist) => {
      try {
        const playlistId = playlist.id || playlist.playlist_id || this.extractPlaylistId(playlist.playlist_url || '');
        if (playlistId) {
          const images = await this.getPlaylistImages(playlistId);
          imageMap[playlistId] = images;
        }
      } catch (error) {
        console.error('❌ Error fetching images for playlist:', playlist, error);
      }
    });

    await Promise.all(promises);
    console.log('✅ Batch image fetch complete, got images for', Object.keys(imageMap).length, 'playlists');
    return imageMap;
  }
}

const spotifyServiceInstance = new SpotifyService();
export default spotifyServiceInstance; 